/**
 * Collapsible "thought process" disclosure, driven by AI SDK reasoning parts
 * (the backend now forwards standard `reasoning` parts via sendReasoning).
 * Hidden when there is no reasoning text.
 */

import React, { useState } from 'react';
import { Box, Collapse, Typography } from '@mui/material';
import PsychologyOutlinedIcon from '@mui/icons-material/PsychologyOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

interface ReasoningDisclosureProps {
  reasoningText: string;
  streaming: boolean;
}

const ReasoningDisclosure: React.FC<ReasoningDisclosureProps> = ({ reasoningText, streaming }) => {
  const [open, setOpen] = useState(false);
  if (!reasoningText.trim()) return null;

  return (
    <Box sx={{ mb: 0.75 }}>
      <Box
        role="button"
        tabIndex={0}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setOpen((v) => !v);
          }
        }}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          cursor: 'pointer',
          color: 'text.secondary',
          userSelect: 'none',
        }}
      >
        <PsychologyOutlinedIcon fontSize="small" />
        <Typography variant="caption" sx={{ fontWeight: 600 }}>
          {streaming ? 'Thinking…' : 'Thought process'}
        </Typography>
        <ExpandMoreIcon
          fontSize="small"
          sx={{
            transition: 'transform 0.15s',
            transform: open ? 'rotate(180deg)' : 'none',
          }}
        />
      </Box>
      <Collapse in={open} unmountOnExit>
        <Typography
          variant="caption"
          component="div"
          sx={{
            mt: 0.5,
            pl: 1.5,
            borderLeft: '2px solid',
            borderColor: 'divider',
            color: 'text.secondary',
            whiteSpace: 'pre-wrap',
            overflowWrap: 'anywhere',
          }}
        >
          {reasoningText}
        </Typography>
      </Collapse>
    </Box>
  );
};

export default React.memo(ReasoningDisclosure);
